import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RotateCcw, Save } from 'lucide-react'
import {
  SMS_MAX_SEGMENTS,
  renderNotification,
  smsInfo,
  validateNotificationTemplate,
  type NotificationTemplate,
  type NotificationVariable,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Input,
  PageHeader,
  Textarea,
  useAuth,
  useToast,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { NOTIFICATIONS_KEY, useTemplates, type TemplateEntry } from '../../lib/notifications'

type Field = 'portal.title' | 'portal.body' | 'email.subject' | 'email.body' | 'sms.body'

/** Éditeur d'un modèle : trois canaux, variables, contrôles en direct, aperçu. */
export function TemplateEditorPage() {
  const { event = '' } = useParams()
  const templates = useTemplates()
  const entry = templates.data?.data.find((t) => t.event === event)
  if (templates.isError) return <Alert tone="danger">{errorMessage(templates.error)}</Alert>
  if (templates.isPending)
    return (
      <Card>
        <CardBody>Chargement…</CardBody>
      </Card>
    )
  if (!entry || !templates.data) return <Alert tone="danger">Événement inconnu.</Alert>
  return (
    <Editor
      key={`${entry.event}-${entry.version}`}
      entry={entry}
      variableLabels={templates.data.variables}
      sample={templates.data.sample}
    />
  )
}

function Editor({
  entry,
  variableLabels,
  sample,
}: {
  entry: TemplateEntry
  variableLabels: Record<NotificationVariable, string>
  sample: Record<NotificationVariable, string>
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<NotificationTemplate>(entry.template)
  const [focused, setFocused] = useState<Field>('email.body')
  const [resetting, setResetting] = useState(false)
  const problems = validateNotificationTemplate(entry.event, draft)
  const errorOf = (field: string) =>
    problems
      .filter((p) => p.field === field)
      .map((p) => p.message)
      .join(' ') || undefined
  const dirty = JSON.stringify(draft) !== JSON.stringify(entry.template)

  const set = (field: Field, value: string) => {
    const [channel, key] = field.split('.') as [keyof NotificationTemplate, string]
    setDraft((d) => ({ ...d, [channel]: { ...d[channel], [key]: value } }))
  }
  const toggle = (channel: keyof NotificationTemplate, enabled: boolean) =>
    setDraft((d) => ({ ...d, [channel]: { ...d[channel], enabled } }))
  const valueOf = (field: Field) => {
    const [channel, key] = field.split('.') as [keyof NotificationTemplate, string]
    return (draft[channel] as unknown as Record<string, string>)[key] ?? ''
  }
  const insert = (variable: NotificationVariable) => {
    const current = valueOf(focused)
    set(focused, `${current}${current && !current.endsWith(' ') ? ' ' : ''}{${variable}}`)
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY })
  const save = useMutation({
    mutationFn: () =>
      session.api.put<TemplateEntry>(`/admin/notifications/templates/${entry.event}`, draft),
    onSuccess: async (t) => {
      toast.success(`Modèle enregistré (version ${t.version})`)
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const reset = useMutation({
    mutationFn: () => session.api.post(`/admin/notifications/templates/${entry.event}/reset`),
    onSuccess: async () => {
      toast.success('Modèle par défaut rétabli')
      setResetting(false)
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const portalSample = sample
  const outboundSample = Object.fromEntries(
    Object.entries(sample).filter(([k]) => entry.variables.includes(k as NotificationVariable))
  )
  const sms = smsInfo(renderNotification(draft.sms.body, outboundSample))
  const textField = (field: Field, label: string, rows?: number) => {
    const props = {
      label,
      value: valueOf(field),
      error: errorOf(field),
      onFocus: () => setFocused(field),
      onChange: (e: { target: { value: string } }) => set(field, e.target.value),
    }
    return rows ? <Textarea {...props} rows={rows} /> : <Input {...props} />
  }

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/notifications/modeles" className="link">
            Modèles de notification
          </Link>
        }
        title={entry.label}
        description={entry.description}
        actions={
          <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {entry.customized && (
              <Button variant="secondary" icon={<RotateCcw />} onClick={() => setResetting(true)}>
                Rétablir le modèle par défaut
              </Button>
            )}
            <Button
              variant="primary"
              icon={<Save />}
              disabled={!dirty || problems.length > 0}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Enregistrer
            </Button>
          </span>
        }
      />
      <div className="template-layout">
        <div className="stack" style={{ gap: 'var(--space-5)' }}>
          <Card>
            <CardHeader title="Variables" />
            <CardBody>
              <p className="muted" style={{ marginTop: 0 }}>
                Cliquez pour insérer dans le champ en cours d’édition.
              </p>
              <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                {[...entry.variables, ...(entry.portalVariables ?? [])].map((v) => (
                  <button
                    key={v}
                    type="button"
                    className="chip"
                    title={variableLabels[v]}
                    onClick={() => insert(v)}
                  >
                    {`{${v}}`}
                    {entry.portalVariables?.includes(v) && (
                      <span className="muted"> · portail seulement</span>
                    )}
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Centre de notifications (portail)"
              actions={
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={draft.portal.enabled}
                    onChange={(e) => toggle('portal', e.target.checked)}
                  />
                  <span>Actif</span>
                </label>
              }
            />
            <CardBody className="stack">
              {textField('portal.title', 'Titre')}
              {textField('portal.body', 'Texte', 3)}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Courriel"
              actions={
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={draft.email.enabled}
                    onChange={(e) => toggle('email', e.target.checked)}
                  />
                  <span>Actif</span>
                </label>
              }
            />
            <CardBody className="stack">
              {textField('email.subject', 'Objet')}
              {textField('email.body', 'Texte (une ligne vide sépare les paragraphes)', 8)}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="SMS"
              actions={
                entry.sms ? (
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={draft.sms.enabled}
                      onChange={(e) => toggle('sms', e.target.checked)}
                    />
                    <span>Actif</span>
                  </label>
                ) : (
                  <Badge variant="neutral">Non autorisé pour cet événement</Badge>
                )
              }
            />
            <CardBody className="stack">
              {entry.sms ? (
                <>
                  {textField('sms.body', 'Texte', 3)}
                  <span className={sms.segments > SMS_MAX_SEGMENTS ? 'field-error' : 'muted'}>
                    {sms.length} caractères · {sms.segments} SMS
                    {sms.unicode ? ' (caractères spéciaux : 70 par SMS)' : ''} — avec des valeurs
                    typiques
                  </span>
                </>
              ) : (
                <p className="muted" style={{ margin: 0 }}>
                  Le SMS est réservé à l’OTP, à la convocation, aux résultats et au complément
                  (ADR-010).
                </p>
              )}
              {errorOf('sms.enabled') && <Alert tone="danger">{errorOf('sms.enabled')}</Alert>}
            </CardBody>
          </Card>
          {errorOf('portal.enabled') && <Alert tone="danger">{errorOf('portal.enabled')}</Alert>}
        </div>

        <aside
          className="stack template-preview"
          aria-label="Aperçu"
          style={{ gap: 'var(--space-4)' }}
        >
          <h2 className="eyebrow">Aperçu (valeurs d’exemple)</h2>
          {draft.portal.enabled && (
            <div className="preview-portal">
              <strong>{renderNotification(draft.portal.title, portalSample)}</strong>
              <p>{renderNotification(draft.portal.body, portalSample)}</p>
            </div>
          )}
          {draft.email.enabled && (
            <div className="preview-mail">
              <div className="preview-mail-subject">
                {renderNotification(draft.email.subject, outboundSample)}
              </div>
              <div className="preview-mail-body">
                {renderNotification(draft.email.body, outboundSample)}
              </div>
            </div>
          )}
          {draft.sms.enabled && entry.sms && (
            <div className="preview-sms">{renderNotification(draft.sms.body, outboundSample)}</div>
          )}
        </aside>
      </div>
      <ConfirmModal
        open={resetting}
        onClose={() => setResetting(false)}
        onConfirm={() => reset.mutate()}
        title="Rétablir le modèle par défaut ?"
        message="La personnalisation est abandonnée (elle reste tracée dans le journal d’audit)."
        confirmLabel="Rétablir"
      />
    </>
  )
}
